import { Link } from "react-router-dom";
import {
  Phone,
  Scissors,
  Sprout,
  Ruler,
  Wind,
  Trash2,
  Calendar,
  Home as HomeIcon,
  Building2,
  ArrowRight,
  Check,
} from "lucide-react";
import { ASSETS, COMPANY, SERVICES } from "@/lib/site";

const ICONS = {
  Scissors,
  Sprout,
  Ruler,
  Wind,
  Trash2,
  Calendar,
  Home: HomeIcon,
  Building2,
};

const PageHero = () => (
  <section
    className="relative pt-32 pb-16 md:pt-40 md:pb-20"
    data-testid="services-hero"
  >
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${ASSETS.photos.mowLines})` }}
    />
    <div className="absolute inset-0 bg-[#0E3A1F]/80" />
    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
      <span className="text-xs uppercase tracking-widest font-bold text-[#5BB23B]">
        Services
      </span>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight">
        Everything your lawn needs.
      </h1>
      <p className="mt-4 text-white/85 text-lg max-w-2xl">
        From a single mow to a full seasonal program, Southern Greens keeps
        your property looking sharp — week after week, season after season.
      </p>
    </div>
  </section>
);

const Services = () => {
  return (
    <div data-testid="services-page" className="bg-white">
      <PageHero />

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((s) => {
            const Icon = ICONS[s.icon] || Sprout;
            return (
              <article
                key={s.slug}
                data-testid={`service-item-${s.slug}`}
                className="group relative bg-white border border-[#D4D4CE] rounded-2xl p-7 hover:-translate-y-1 transition-all duration-300 hover:shadow-xl hover:border-[#5BB23B]/50"
              >
                <div className="h-12 w-12 rounded-xl bg-[#0E3A1F]/5 text-[#0E3A1F] flex items-center justify-center group-hover:bg-[#5BB23B] group-hover:text-white transition-colors">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display font-bold text-2xl text-[#0A0A0A]">
                  {s.title}
                </h3>
                <p className="mt-3 text-[#4B5563] leading-relaxed">
                  {s.description}
                </p>
                <ul className="mt-5 space-y-2">
                  <li className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                    <Check className="h-4 w-4 text-[#5BB23B]" /> Free, no-pressure quote
                  </li>
                  <li className="flex items-center gap-2 text-sm text-[#0A0A0A]">
                    <Check className="h-4 w-4 text-[#5BB23B]" /> Residential &amp; commercial
                  </li>
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section
        className="relative py-20 overflow-hidden"
        data-testid="services-cta"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${ASSETS.photos.sideYardStripes})` }}
        />
        <div className="absolute inset-0 bg-[#0E3A1F]/85" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="font-display text-3xl md:text-5xl font-bold leading-tight">
            Don’t see exactly what you need?
          </h2>
          <p className="mt-4 text-white/85 text-lg">
            Give us a call — chances are we do it. We’re happy to put together
            a custom plan that fits your yard and your budget.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={COMPANY.telHref}
              data-testid="services-call-link"
              className="inline-flex items-center gap-3 bg-white text-[#0E3A1F] rounded-full px-7 py-4 font-bold text-lg shadow-xl hover:-translate-y-0.5 transition-all"
            >
              <Phone className="h-5 w-5" /> {COMPANY.phoneDisplay}
            </a>
            <Link
              to="/quote"
              data-testid="services-quote-button"
              className="sg-btn-primary inline-flex items-center gap-2 rounded-full px-7 py-4 font-bold text-lg"
            >
              Request a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
